import React, { useState, useEffect } from 'react';
import { getDuplicatesByBarcodeRequest, getDuplicatesBySkuAndBarcodeRequest, ProductLoadingKeys } from "../../slices/productsSlice";
import { useDispatch, useSelector } from "react-redux";
import { arrayHasItems } from "../../helpers/common";
import { LoaderSpinner } from "../../components/LoaderSpinner";
import axios from 'axios';

const DuplicatesReports = () => {
    const dispatch = useDispatch();
    const [activeOnly, setActiveOnly] = useState(false);

    const {
        duplicatesBarcodeAndSku,
        duplicatesBarcode,
        loadingKeys,
        loading
    } = useSelector((state) => state.products);

    useEffect(() => {
        dispatch(getDuplicatesBySkuAndBarcodeRequest({ activeOnly }));
        dispatch(getDuplicatesByBarcodeRequest({ activeOnly }));
    }, [activeOnly]);

    const skuPlusBarcode = arrayHasItems(duplicatesBarcodeAndSku) ? duplicatesBarcodeAndSku : [];
    const onlyBarcode = arrayHasItems(duplicatesBarcode) ? duplicatesBarcode : [];

    const reportDataIsLoading = () => 
        loading && loadingKeys.includes(ProductLoadingKeys.getDuplicatesBarcode) ||
        loading && loadingKeys.includes(ProductLoadingKeys.getDuplicatesBySkuAndBarcode);

    const [expandedRows, setExpandedRows] = useState({});
    const [expandedSections, setExpandedSections] = useState({
        skuAndBarcode: true,
        barcodeOnly: true
    });

    const toggleRowExpansion = (id) => {
        setExpandedRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const renderTable = (data, prefix) => (
        <div className="bg-white rounded-lg overflow-hidden">
            <table className="w-full">
                <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[40px]"></th>
                        <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[100px]">Items Count</th>
                        <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                        <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Barcode</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((group, index) => (
                        <React.Fragment key={index}>
                            <tr 
                                className={`
                                    border-b border-gray-200 last:border-b-0
                                    cursor-pointer
                                    transition-colors duration-150
                                    ${expandedRows[`${prefix}_${index}`] 
                                        ? 'bg-indigo-50/80 hover:bg-indigo-50' 
                                        : `${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'} hover:bg-gray-50/50`
                                    }
                                `}
                                onClick={() => toggleRowExpansion(`${prefix}_${index}`)}
                            >
                                <td className="py-3 px-4 text-sm text-gray-600">
                                    <div
                                        className={`
                                            transition-colors duration-150
                                            ${expandedRows[`${prefix}_${index}`] 
                                                ? 'text-indigo-500 hover:text-indigo-600' 
                                                : 'text-gray-400 hover:text-gray-600'
                                            }
                                        `}
                                    >
                                        <svg 
                                            className={`w-5 h-5 transition-transform ${expandedRows[`${prefix}_${index}`] ? 'rotate-90' : ''}`} 
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </td>
                                <td className="py-3 px-4 text-sm text-gray-900">{group[0].name}</td>
                                <td className="py-3 px-4 text-sm text-gray-500">{group.length} items</td>
                                <td className="py-3 px-4 text-sm text-gray-500 font-mono">{group[0].sku}</td>
                                <td className="py-3 px-4 text-sm text-gray-500 font-mono">{group[0].barcode}</td>
                            </tr>
                            {expandedRows[`${prefix}_${index}`] && (
                                <tr>
                                    <td colSpan="5" className="p-0">
                                        <div className="pl-[50px] pr-4 py-4 bg-white border-b border-gray-200">
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="border-b border-gray-200">
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Barcode</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Eden Status</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Line Items</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {group.map((item) => (
                                                        <tr key={item.id} className="border-b border-gray-100 last:border-b-0">
                                                            <td className="px-4 py-2 text-sm font-mono text-gray-600">{item.id}</td>
                                                            <td className="px-4 py-2 text-sm text-gray-900">{item.name}</td>
                                                            <td className="px-4 py-2 text-sm text-gray-900 font-mono">{item.sku}</td>
                                                            <td className="px-4 py-2 text-sm text-gray-900 font-mono">{item.barcode}</td>
                                                            <td className="px-4 py-2 text-sm text-gray-900">
                                                                <span className={`
                                                                    px-2 py-1 rounded-full text-xs font-medium
                                                                    ${item.eden_status?.toLowerCase() === 'active' ? 'bg-green-100 text-green-800' : ''}
                                                                    ${item.eden_status?.toLowerCase() === 'inactive' ? 'bg-red-100 text-red-800' : ''}
                                                                    ${item.eden_status?.toLowerCase() === 'deleted' ? 'bg-gray-100 text-gray-800' : ''}
                                                                    ${!item.eden_status ? 'bg-gray-100 text-gray-800' : ''}
                                                                `}>
                                                                    {item.eden_status || 'N/A'}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-2 text-sm text-gray-900 w-[300px]">
                                                                {item.line_items?.length > 0 ? (
                                                                    <div className="flex flex-col gap-1">
                                                                        {item.line_items.map((lineItem, lineIndex) => (
                                                                            <div key={lineItem.po_line_item_id} className="flex items-baseline gap-2">
                                                                                <span className="text-gray-500 w-4">{lineIndex + 1}.</span>
                                                                                <span className="font-mono">
                                                                                    PO №: {lineItem.po_number} (Line item ID - {lineItem.po_line_item_id})
                                                                                </span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-gray-400 italic">No line items</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const handleExportCSV = async (type) => {
        try {
            const response = await axios.get(`/api/v1/products/export-duplicates?type=${type}`, {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `duplicates-${type}-${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to export CSV:', error);
            // You might want to add a toast notification here
        }
    };

    const renderFilterButton = () => (
        <button
            onClick={() => setActiveOnly(!activeOnly)}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors duration-150 ${
                activeOnly 
                ? 'bg-green-500 hover:bg-green-400 text-white' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
        >
            {activeOnly ? 'Show All' : 'Active Only'}
        </button>
    );

    return (
        <main className="w-full h-full px-4 sm:px-6 lg:px-8">
            {reportDataIsLoading() ? (
                <div className="h-5/6 w-full absolute">
                    <LoaderSpinner colorSpinner="black" width="70" height="70" border="5" />
                </div>
            ) : (
                <div className="space-y-4 my-4">
                    {renderFilterButton()}
                    {/* SKU & Barcode Duplicates Section */}
                    {arrayHasItems(skuPlusBarcode) && (
                        <div>
                            <button
                                onClick={() => toggleSection('skuAndBarcode')}
                                className="w-full text-left"
                            >
                                <div className="bg-blue-600 text-white px-4 py-2 flex items-center justify-between rounded-t-lg">
                                    <div>
                                        <h2 className="text-lg font-semibold">SKU & Barcode Duplicates</h2>
                                        <p className="text-sm text-blue-100">Products with identical SKUs and barcodes</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleExportCSV('sku-barcode');
                                            }}
                                            className="px-3 py-1 bg-blue-500 hover:bg-blue-400 rounded-md text-sm font-medium transition-colors duration-150"
                                        >
                                            Export CSV
                                        </button>
                                        <span className="bg-blue-500 px-3 py-1 rounded-full text-sm">
                                            {skuPlusBarcode.length} groups
                                        </span>
                                        <svg 
                                            className={`w-6 h-6 transition-transform ${expandedSections.skuAndBarcode ? 'rotate-180' : ''}`}
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </button>
                            {expandedSections.skuAndBarcode && renderTable(skuPlusBarcode, 'sku')}
                        </div>
                    )}

                    {/* Barcode Only Duplicates Section */}
                    {arrayHasItems(onlyBarcode) && (
                        <div>
                            <button
                                onClick={() => toggleSection('barcodeOnly')}
                                className="w-full text-left"
                            >
                                <div className="bg-purple-600 text-white px-4 py-2 flex items-center justify-between rounded-t-lg">
                                    <div>
                                        <h2 className="text-lg font-semibold">Barcode Duplicates</h2>
                                        <p className="text-sm text-purple-100">Products with identical barcodes only</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleExportCSV('barcode');
                                            }}
                                            className="px-3 py-1 bg-purple-500 hover:bg-purple-400 rounded-md text-sm font-medium transition-colors duration-150"
                                        >
                                            Export CSV
                                        </button>
                                        <span className="bg-purple-500 px-3 py-1 rounded-full text-sm">
                                            {onlyBarcode.length} groups
                                        </span>
                                        <svg 
                                            className={`w-6 h-6 transition-transform ${expandedSections.barcodeOnly ? 'rotate-180' : ''}`}
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </button>
                            {expandedSections.barcodeOnly && renderTable(onlyBarcode, 'barcode')}
                        </div>
                    )}
                </div>
            )}
        </main>
    );
};

export default DuplicatesReports;